'use client';

import { useState } from "react"
import { Calendar, Save, MapPin, Clock, ArrowRight, Sparkles, TreePalm, ArrowLeft } from "lucide-react"
import CompactHeader from "../CompactHeader"
import { useNavigate } from '@tanstack/react-router'
import { 
  Button, 
  Input,
  useRepository, 
} from '@city-sage/aurum-avis-labs-components'
import { useForm } from "react-hook-form"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from 'react-i18next'
import { QueryKeys } from "../../lib/QueryKeys"
import { toast } from "sonner";
import { useIsAuthenticated } from "@azure/msal-react";

interface CreateManualItineraryForm {
  title: string
  numberOfDays: number
}

export const CreateManualItineraryPage = () => {
  const navigate = useNavigate()
  const { api } = useRepository()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const [selectedDayOption, setSelectedDayOption] = useState<"quick" | "custom" | "flexible">("quick")
  const isAuthenticated = useIsAuthenticated();
  // Form setup
  const form = useForm<CreateManualItineraryForm>({
    defaultValues: {
      title: "",
      numberOfDays: 3,
    },
  })

  const { data: account } = useQuery({
    queryKey: [QueryKeys.account.base],
    queryFn: () => api.getMyAccount(),
    enabled: !!isAuthenticated
  })

  // Create itinerary and days mutation
  const createItineraryMutation = useMutation({
    mutationFn: async (data: CreateManualItineraryForm) => {
      // First create the itinerary
      const newItinerary = await api.createItinerary({
        createItineraryRequest: {
          title: data.title,
          shortDescription: "",
          description: "",
          highlights: "",
          accountId: account?.id!,
        }
      })

      // Then create the days (only if not flexible)
      if (selectedDayOption !== "flexible") {
        const dayPromises = Array.from({ length: data.numberOfDays }, (_, index) =>
          api.createItineraryDay({
            itineraryId: newItinerary.id!,
            createDayDto: {
              number: index + 1,
              title: `Day ${index + 1}`,
              description: "",
            },
          })
        )
        await Promise.all(dayPromises)
      }

      return newItinerary
    },
    onSuccess: (newItinerary) => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.itinerary.base })
      navigate({ to: `/itineraries/${newItinerary.id}/edit` })
      toast.success("Itinerary created successfully")
    },
    onError: (error) => {
      console.error("Failed to create itinerary:", error)
      toast.error("Failed to create itinerary")
    },
  })

  const handleSubmit = form.handleSubmit(async (data) => {
    createItineraryMutation.mutate(data)
  })

  const { register, watch, setValue } = form
  const formValues = watch()

  const quickDayOptions = [
    { days: 1, label: t("components.createManualItinerary.durationSection.options.oneDay.label"), subtitle: t("components.createManualItinerary.durationSection.options.oneDay.subtitle") },
    { days: 2, label: t("components.createManualItinerary.durationSection.options.twoDays.label"), subtitle: t("components.createManualItinerary.durationSection.options.twoDays.subtitle") },
    { days: 3, label: t("components.createManualItinerary.durationSection.options.threeDays.label"), subtitle: t("components.createManualItinerary.durationSection.options.threeDays.subtitle") },
    { days: 4, label: t("components.createManualItinerary.durationSection.options.fourDays.label"), subtitle: t("components.createManualItinerary.durationSection.options.fourDays.subtitle") },
    { days: 5, label: t("components.createManualItinerary.durationSection.options.fiveDays.label"), subtitle: t("components.createManualItinerary.durationSection.options.fiveDays.subtitle") },
    { days: 7, label: t("components.createManualItinerary.durationSection.options.oneWeek.label"), subtitle: t("components.createManualItinerary.durationSection.options.oneWeek.subtitle") },
    { days: 10, label: t("components.createManualItinerary.durationSection.options.tenDays.label"), subtitle: t("components.createManualItinerary.durationSection.options.tenDays.subtitle") },
    { days: 14, label: t("components.createManualItinerary.durationSection.options.twoWeeks.label"), subtitle: t("components.createManualItinerary.durationSection.options.twoWeeks.subtitle") },
  ]

  return (
    <div className="min-h-dvh bg-gray-50">
      <CompactHeader 
        backTo="/itineraries/create"
        icon={Calendar}
        title="Create Manual Itinerary"
        subtitle="Build your custom travel plan"
      />

      {/* Form Section */}
      <div className="space-y-2">
        <form onSubmit={handleSubmit} className="space-y-2">
          {/* Title Input */}
          <div className="bg-white px-6 py-6">
            <div className="flex items-center gap-2 mb-4">
              <label className="text-lg font-semibold text-gray-900">1. {t("components.createManualItinerary.titleSection.label")}</label>
            </div>

            <Input
              placeholder={t("components.createManualItinerary.titleSection.placeholder")}
              {...register("title")}
              className="w-full text-lg px-4 py-3 mb-3"
              required
            />

            <div className="text-sm text-gray-500">
              {t("components.createManualItinerary.titleSection.helpText")}
            </div>
          </div>

          {/* Duration Selection */}
          <div className="bg-white px-6 py-6">
            <div className="flex items-center gap-2 mb-4">
              <label className="text-lg font-semibold text-gray-900">2. {t("components.createManualItinerary.durationSection.label")}</label>
            </div>

            {/* Duration Type Selector */}
            <div className="grid grid-cols-3 gap-2 p-1 bg-gray-100 rounded-xl mb-6">
                <button
                  type="button"
                  onClick={() => setSelectedDayOption("quick")}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    selectedDayOption === "quick"
                      ? "bg-white text-primary shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {t("components.createManualItinerary.durationSection.quickPick")}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedDayOption("custom")}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    selectedDayOption === "custom"
                      ? "bg-white text-primary shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {t("components.createManualItinerary.durationSection.custom")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDayOption("flexible")
                    setValue("numberOfDays", 1)
                  }}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    selectedDayOption === "flexible"
                      ? "bg-white text-primary shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {t("components.createManualItinerary.durationSection.flexible")}
                </button>
              </div>

            {/* Quick Pick Options */}
            {selectedDayOption === "quick" && (
              <div className="grid grid-cols-3 gap-2 mb-4">
                {quickDayOptions.map((option) => (
                  <button
                    key={option.days}
                    type="button"
                    onClick={() => setValue("numberOfDays", option.days)}
                    className={`p-2.5 rounded-lg border-2 text-left transition-all ${
                      formValues.numberOfDays === option.days
                        ? "border-primary bg-primary/10"
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                  >
                    <div
                      className={`font-medium text-sm ${
                        formValues.numberOfDays === option.days ? "text-primary" : "text-gray-900"
                      }`}
                    >
                      {option.label}
                    </div>
                    <div
                      className={`text-xs ${
                        formValues.numberOfDays === option.days ? "text-primary/80" : "text-gray-500"
                      }`}
                    >
                      {option.subtitle}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Custom Input */}
            {selectedDayOption === "custom" && (
              <div className="space-y-4">
                <div className="relative">
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    placeholder={t("components.createManualItinerary.durationSection.customPlaceholder")}
                    {...register("numberOfDays", {
                      valueAsNumber: true,
                      min: { value: 1, message: "Minimum 1 day" },
                      max: { value: 365, message: "Maximum 365 days" }
                    })}
                    className="w-full text-lg px-4 py-3"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{t("components.createManualItinerary.summary.daysText")}</div>
                </div>
                <div className="text-sm text-gray-500 text-center">{t("components.createManualItinerary.durationSection.customHelpText")}</div>
              </div>
            )}

            {/* Flexible Option */}
            {selectedDayOption === "flexible" && (
              <div className="text-center py-6 space-y-3">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto">
                  <TreePalm className="h-6 w-6 text-white" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-900">{t("components.createManualItinerary.durationSection.flexibleTitle")}</h3>
                  <p className="text-sm text-gray-600">
                    {t("components.createManualItinerary.durationSection.flexibleDescription")}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="bg-white px-6 py-6">
            <Button
              type="submit"
              className="w-full py-4 text-lg font-semibold"
              disabled={createItineraryMutation.isPending || !formValues.title.trim()}
            >
              {createItineraryMutation.isPending ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{t("components.createManualItinerary.actions.creating")}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Save className="h-5 w-5" />
                  <span>{t("components.createManualItinerary.actions.startBuilding")}</span>
                </div>
              )}
            </Button>

            {/* Help Text */}
            <div className="text-center text-sm text-gray-500 space-y-1 mt-4">
              <p>{t("components.createManualItinerary.helpText.line1")}</p>
              <p>{t("components.createManualItinerary.helpText.line2")}</p>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
} 